// main.ts — entry point.
//
//   1. Register routes (real pages + stubs for the rest).
//   2. Mount site header + footer.
//   3. Boot auth (in the background — does not block first render).
//   4. Wire the hash-change handler that renders the current route.

import { mountHeader, setChromeVisible } from './layout/header';
import { mountFooter } from './layout/footer';
import { bootAuth, getUser, getUserRole, onUserChange } from './auth';
import { registerRoute, getRoute, parseHash, navigate, peekPageData } from './router';
import type { PageContext } from './router';

import LandingPage from './pages/landing';
import AboutPage from './pages/about';
import GrantInfoPage from './pages/grant-info';
import PublicGrantsPage from './pages/public-grants';
import UserDashboard from './pages/user-dashboard';
import GrantSuccessPage from './pages/grant-success';
import GrantForm from './pages/grant-form';
import GrantDetailPage from './pages/grant-detail';
import AdminDashboard from './pages/admin-dashboard';
import GrantReviewPage from './pages/grant-review';
import UserManagementPage from './pages/user-management';
import PayoutPage from './pages/payout';
import { makeLoginPage } from './pages/login';
import { makeAuthCardPage } from './pages/auth-card';
import { makeStub } from './pages/_stub';

// ─── Routes (real pages + stubs) ────────────────────────────────
registerRoute('landing',         LandingPage);
registerRoute('about',           AboutPage);
registerRoute('grant-info',      GrantInfoPage);
registerRoute('public-grants',   PublicGrantsPage);
registerRoute('login',           makeLoginPage('signin'));
registerRoute('signup',          makeLoginPage('signup'));
registerRoute('verify-email',    makeAuthCardPage({
  eyebrow: 'Verify email',
  title:   'Email verification',
  body:    'The 6-digit code flow is part of the email/password path that lands with Firebase auth. Demo accounts skip verification.',
  ctaLabel: 'Back to sign in',
  ctaRoute: 'login',
}));
registerRoute('verified',        makeAuthCardPage({
  eyebrow: 'Verified',
  title:   "You're verified.",
  body:    'Your account has been confirmed. Welcome to The Bray Family Foundation.',
  ctaLabel: 'Go to sign in',
  ctaRoute: 'login',
  ctaArrow: true,
}));
registerRoute('forgot-password', makeAuthCardPage({
  eyebrow: 'Forgot password',
  title:   'Forgot password?',
  body:    'Password reset will be available once email/password auth is wired through Firebase. Until then, just pick a demo account on the sign-in page.',
  ctaLabel: 'Back to sign in',
  ctaRoute: 'login',
}));
registerRoute('reset-password',  makeAuthCardPage({
  eyebrow: 'Reset password',
  title:   'Set a new password.',
  body:    'Password reset lands with Firebase email/password auth.',
  ctaLabel: 'Back to sign in',
  ctaRoute: 'login',
}));
registerRoute('user-dashboard',  UserDashboard);
registerRoute('grant-form',      GrantForm);
registerRoute('grant-success',   GrantSuccessPage);
registerRoute('grant-detail',    GrantDetailPage);
registerRoute('admin-dashboard', AdminDashboard);
registerRoute('grant-review',    GrantReviewPage);
registerRoute('user-management', UserManagementPage);
registerRoute('payout',          PayoutPage);

// _stub is now only used during exploratory routing — no live routes use it.
void makeStub;

// ─── Page outlet wiring ─────────────────────────────────────────
const pageEl = document.getElementById('page') as HTMLElement | null;
if (!pageEl) throw new Error('frontend3: missing <main id="page"> outlet');

let currentPageName = '';

function buildContext(name: string, params: URLSearchParams): PageContext {
  return {
    params,
    pageData: peekPageData(),
    user:     getUser(),
    userRole: getUserRole(),
    navigate,
  };
}

function renderCurrent(): void {
  const { name, params } = parseHash(location.hash);
  const route = getRoute(name) ?? LandingPage;

  // Build ctx, then run guard
  let ctx = buildContext(name, params);
  if (route.guard) {
    const redirect = route.guard(ctx);
    if (redirect) {
      navigate(redirect);
      return;
    }
  }

  // Full-screen routes hide header/footer.
  setChromeVisible(!route.fullScreen);

  // Tear down previous page.
  const prev = getRoute(currentPageName);
  if (prev?.unmount) try { prev.unmount(); } catch (e) { console.error(e); }

  pageEl!.innerHTML = route.render(ctx);
  if (route.mount) {
    try { route.mount(pageEl!, ctx); } catch (e) { console.error(e); }
  }

  currentPageName = name;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

// Delegated navigation: any [data-nav="route"] click within the page
// outlet triggers navigate(). Pages don't have to wire their own.
pageEl.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;
  const navEl = target.closest('[data-nav]') as HTMLElement | null;
  if (navEl) {
    e.preventDefault();
    navigate(navEl.getAttribute('data-nav')!);
  }
});

window.addEventListener('hashchange', renderCurrent);

// ─── Boot ───────────────────────────────────────────────────────
mountHeader();
mountFooter();
renderCurrent();

// React to sign-in state changes. This is the single place post-login
// navigation happens (mirrors frontend2's central handleLogin): when a
// user becomes signed in while on an auth route, send them to their
// dashboard. The login page itself doesn't navigate — its mount/unmount
// `alive` flag would otherwise be flipped by this very re-render before
// it could redirect. Any other state change just re-renders so user-aware
// chrome (header, CTAs) updates.
onUserChange(() => {
  const user = getUser();
  const { name } = parseHash(location.hash);
  if (user && (name === 'login' || name === 'signup')) {
    const role = getUserRole();
    navigate(role === 'admin' || role === 'approver' ? 'admin-dashboard' : 'user-dashboard');
    return;
  }
  renderCurrent();
});

void bootAuth();

// Dev affordance — mirrors frontend2's window.__nav.
window.__nav = navigate;

// Ensure landing is the default when no hash is present.
if (!location.hash) {
  history.replaceState(null, '', '#/landing');
  renderCurrent();
}
